import { FormEvent, useContext, useState } from "react";
import { Alert, Button, Col, Form, Modal, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import type { HouseholdGroupResponse } from "../../../../api/src/household-groups/household-groups.service";
import Loading from "../../Loading";
import {
  useQueryBillingMinimums,
  useQueryFeeStructures,
} from "../../billing/lib";
import { NotificationContext } from "../../Notifications";
import ActionButton from "../../components/ActionButton";
import SubmitButton from "../../components/SubmitButton";
import { serverError } from "../../lib/api";
import { toISODateNoTimezone } from "../../lib/date";
import { InlineError, displayAccountName } from "../../lib/display";
import { useQueryAccounts } from "../account/lib";
import {
  useClearHouseholdGroupPayer,
  useDesignateHouseholdGroupPayer,
  useDissolveHouseholdGroup,
  useNotifyHouseholdGroupUpdated,
  useUpdateHouseholdGroup,
} from "./lib";

/** The select values are strings; "" is the billing minimum's "None". */
type HouseholdGroupBillingForm = {
  feeStructureId: string;
  billingMinimumId: string;
};

const byName = (a: { name: string }, b: { name: string }) =>
  a.name < b.name ? -1 : a.name > b.name ? 1 : 0;

/**
 * The api's refusal text when it sent one, else the app's generic text. No
 * other words are added to the api's text.
 */
const failureText = (err: unknown): string => {
  const message = serverError(err)?.body?.message;
  return typeof message === "string" ? message : "An error occurred";
};

type HouseholdGroupBillingGroup = Pick<
  HouseholdGroupResponse,
  | "householdGroupId"
  | "name"
  | "feeStructureId"
  | "billingMinimumId"
  | "payerAccountId"
  | "dissolvedEffectiveDate"
  | "members"
>;

/**
 * The group's fee structure and billing minimum, which serve as the billing
 * configuration of its households while grouped
 * (s-household-groups-group-level-019f18f8, HG-R16). The fee structure select
 * has no "None" option: the api refuses to clear a group's fee structure.
 *
 * A group with a `dissolvedEffectiveDate` takes no write: its three selects
 * are disabled and show its values, and no "Save" is offered.
 */
const HouseholdGroupBilling = ({
  group,
}: {
  group: HouseholdGroupBillingGroup;
}) => {
  const {
    isPending: isPendingFeeStructures,
    isError: isErrorFeeStructures,
    data: dataFeeStructures,
  } = useQueryFeeStructures();
  const feeStructures = [...(dataFeeStructures?.data ?? [])].sort(byName);

  const {
    isPending: isPendingBillingMinimums,
    isError: isErrorBillingMinimums,
    data: dataBillingMinimums,
  } = useQueryBillingMinimums();
  const billingMinimums = [...(dataBillingMinimums?.data ?? [])].sort(byName);

  return (
    <>
      <Row>
        <Col>
          <h3>Billing Settings</h3>
        </Col>
      </Row>
      {isPendingFeeStructures || isPendingBillingMinimums ? (
        <Loading />
      ) : (
        <HouseholdGroupBillingFormBody
          group={group}
          feeStructures={isErrorFeeStructures ? null : feeStructures}
          billingMinimums={isErrorBillingMinimums ? null : billingMinimums}
        />
      )}
      {/* Remounted when the group's payer changes, so the select shows it. */}
      <HouseholdGroupPayerAccount
        key={group.payerAccountId ?? ""}
        group={group}
      />
      {group.dissolvedEffectiveDate == null ? (
        <HouseholdGroupDissolve
          householdGroupId={group.householdGroupId}
          name={group.name}
        />
      ) : null}
    </>
  );
};

/**
 * The payer account: one account of a household whose membership of the group
 * has no end date, or "None" (no payer account is designated, HG-R5b). "None"
 * is the only way to clear the payer account. The client does not pre-check
 * an archived account: the api refuses and the row shows its text.
 */
const HouseholdGroupPayerAccount = ({
  group,
}: {
  group: HouseholdGroupBillingGroup;
}) => {
  const [payerAccountId, setPayerAccountId] = useState(
    group.payerAccountId?.toString() ?? "",
  );
  const [submitError, setSubmitError] = useState<string | null>(null);
  const isDissolved = group.dissolvedEffectiveDate != null;

  const {
    isPending,
    isError,
    data: accountsBody,
  } = useQueryAccounts({ includeBalances: true, pageSize: 10000 });

  const openMemberHouseholdIds = new Set(
    (group.members ?? [])
      .filter((member) => member.endDate == null)
      .map((member) => member.householdId),
  );
  const eligibleAccounts = (accountsBody?.data ?? []).filter(
    (account) =>
      account.householdId != null &&
      openMemberHouseholdIds.has(account.householdId),
  );

  const designatePayer = useDesignateHouseholdGroupPayer(
    group.householdGroupId,
  );
  const clearPayer = useClearHouseholdGroupPayer(group.householdGroupId);
  const notifyUpdated = useNotifyHouseholdGroupUpdated(
    group.householdGroupId,
    group.name,
  );

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);
    try {
      if (payerAccountId === "") {
        await clearPayer.mutateAsync();
      } else {
        await designatePayer.mutateAsync({
          accountId: parseInt(payerAccountId),
        });
      }
      notifyUpdated();
    } catch (err) {
      console.error("Failed to save household group payer account", err);
      setSubmitError(failureText(err));
    }
  };

  return (
    <Form onSubmit={onSubmit}>
      {submitError === null ? null : (
        <Alert variant="danger">{submitError}</Alert>
      )}
      <Row className="mb-3">
        <Col xl={7}>
          <Row>
            <Col as="dt" xxl={4} md={6}>
              <Form.Label htmlFor="household-group-payerAccountId">
                Payer Account
              </Form.Label>
            </Col>
            <Col as="dd" xxl={6} md={6}>
              {isPending ? (
                <Loading />
              ) : isError ? (
                <Alert variant="danger">An error occurred</Alert>
              ) : (
                <Form.Select
                  id="household-group-payerAccountId"
                  value={payerAccountId}
                  onChange={(event) => setPayerAccountId(event.target.value)}
                  disabled={isDissolved}
                >
                  <option value="">None</option>
                  {eligibleAccounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {displayAccountName(
                        account.displayName,
                        account.displayNumber,
                      )}
                    </option>
                  ))}
                </Form.Select>
              )}
            </Col>
          </Row>
        </Col>
      </Row>
      {isPending || isError || isDissolved ? null : (
        <Row className="mb-3">
          <Col>
            <SubmitButton
              isSubmitting={designatePayer.isPending || clearPayer.isPending}
            />
          </Col>
        </Row>
      )}
    </Form>
  );
};

/**
 * Dissolves the group from an effective date. The form mounts only while its
 * modal is shown, so each opening starts at today's date. On a refusal the
 * modal stays open and shows the api's text; the client does not pre-check a
 * membership that starts after the date.
 */
const HouseholdGroupDissolveForm = ({
  householdGroupId,
  name,
  onHide,
}: {
  householdGroupId: number;
  name: string;
  onHide: () => void;
}) => {
  const [effectiveDate, setEffectiveDate] = useState(() =>
    toISODateNoTimezone(new Date()),
  );
  const [submitError, setSubmitError] = useState<string | null>(null);
  const dissolve = useDissolveHouseholdGroup(householdGroupId);
  const notifyUpdated = useNotifyHouseholdGroupUpdated(householdGroupId, name);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);
    try {
      await dissolve.mutateAsync({ effectiveDate });
      notifyUpdated();
      onHide();
    } catch (err) {
      console.error("Failed to dissolve household group", err);
      setSubmitError(failureText(err));
    }
  };

  return (
    <Form onSubmit={onSubmit}>
      <Modal.Body>
        {submitError === null ? null : (
          <Alert variant="danger">{submitError}</Alert>
        )}
        <Form.Group
          className="mb-3"
          controlId="household-group-dissolve-effectiveDate"
        >
          <Form.Label>Effective Date</Form.Label>
          <Form.Control
            type="date"
            value={effectiveDate}
            onChange={(event) => setEffectiveDate(event.target.value)}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <SubmitButton label="Archive" isSubmitting={dissolve.isPending} />
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
      </Modal.Footer>
    </Form>
  );
};

/** "Dissolve" and its modal, for a group with no `dissolvedEffectiveDate`. */
const HouseholdGroupDissolve = ({
  householdGroupId,
  name,
}: {
  householdGroupId: number;
  name: string;
}) => {
  const [show, setShow] = useState(false);
  const onHide = () => setShow(false);

  return (
    <>
      <Row className="mb-3">
        <Col>
          <ActionButton
            variant="secondary"
            label="Archive"
            type="button"
            onClick={() => setShow(true)}
          />
        </Col>
      </Row>
      <Modal show={show} onHide={onHide}>
        <Modal.Header closeButton>
          <Modal.Title>Archive Household Group</Modal.Title>
        </Modal.Header>
        {show ? (
          <HouseholdGroupDissolveForm
            householdGroupId={householdGroupId}
            name={name}
            onHide={onHide}
          />
        ) : null}
      </Modal>
    </>
  );
};

/**
 * The two selects and "Save". A list that failed to load is `null`: its row
 * shows the load error, and no "Save" is offered. For a group with a
 * `dissolvedEffectiveDate` the selects are disabled and no "Save" is offered.
 */
const HouseholdGroupBillingFormBody = ({
  group,
  feeStructures,
  billingMinimums,
}: {
  group: Pick<
    HouseholdGroupResponse,
    | "householdGroupId"
    | "name"
    | "feeStructureId"
    | "billingMinimumId"
    | "dissolvedEffectiveDate"
  >;
  feeStructures: { feeStructureId: number; name: string }[] | null;
  billingMinimums: { id: number; name: string }[] | null;
}) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<HouseholdGroupBillingForm>({
    defaultValues: {
      // A group with no fee structure shows the first one as chosen, as the
      // select has no "None" option; the form value must be the same one.
      feeStructureId:
        group.feeStructureId?.toString() ??
        feeStructures?.[0]?.feeStructureId.toString() ??
        "",
      billingMinimumId: group.billingMinimumId?.toString() ?? "",
    },
  });

  const isDissolved = group.dissolvedEffectiveDate != null;

  const updateHouseholdGroup = useUpdateHouseholdGroup(group.householdGroupId);
  const notificationContext = useContext(NotificationContext);
  const notifyUpdated = useNotifyHouseholdGroupUpdated(
    group.householdGroupId,
    group.name,
  );

  const onSubmit = async (data: HouseholdGroupBillingForm) => {
    try {
      const updated = await updateHouseholdGroup.mutateAsync({
        feeStructureId: parseInt(data.feeStructureId),
        billingMinimumId:
          data.billingMinimumId === "" ? null : parseInt(data.billingMinimumId),
      });
      reset({
        feeStructureId: updated.feeStructureId?.toString() ?? "",
        billingMinimumId: updated.billingMinimumId?.toString() ?? "",
      });
      notifyUpdated();
    } catch (err) {
      console.error("Failed to save household group", err);
      const message = serverError(err)?.body?.message;
      notificationContext.pushNotification({
        id: `household-group-${group.householdGroupId}`,
        header: "Failed to Save Household Group",
        body:
          typeof message === "string" ? message : `${group.name} was not saved`,
        variant: "danger",
      });
    }
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Row className="mb-3">
        <Col xl={7}>
          <Row>
            <Col as="dt" xxl={4} md={6}>
              <Form.Label htmlFor="household-group-feeStructureId">
                Assigned Fee Structure
              </Form.Label>
            </Col>
            <Col as="dd" xxl={6} md={6}>
              {feeStructures === null ? (
                <InlineError>
                  There was an error loading fee structures
                </InlineError>
              ) : (
                <Form.Select
                  id="household-group-feeStructureId"
                  {...register("feeStructureId")}
                  disabled={isDissolved}
                >
                  {feeStructures.map((feeStructure) => (
                    <option
                      key={feeStructure.feeStructureId}
                      value={feeStructure.feeStructureId}
                    >
                      {feeStructure.name}
                    </option>
                  ))}
                </Form.Select>
              )}
            </Col>
          </Row>
          <Row>
            <Col as="dt" xxl={4} md={6}>
              <Form.Label htmlFor="household-group-billingMinimumId">
                Assigned Billing Minimum
              </Form.Label>
            </Col>
            <Col as="dd" xxl={6} md={6}>
              {billingMinimums === null ? (
                <InlineError>
                  There was an error loading billing minimums
                </InlineError>
              ) : (
                <Form.Select
                  id="household-group-billingMinimumId"
                  {...register("billingMinimumId")}
                  disabled={isDissolved}
                >
                  <option value="">None</option>
                  {billingMinimums.map((billingMinimum) => (
                    <option key={billingMinimum.id} value={billingMinimum.id}>
                      {billingMinimum.name}
                    </option>
                  ))}
                </Form.Select>
              )}
            </Col>
          </Row>
        </Col>
      </Row>
      {feeStructures === null ||
      billingMinimums === null ||
      isDissolved ? null : (
        <Row className="mb-3">
          <Col>
            <SubmitButton isSubmitting={isSubmitting} />
          </Col>
        </Row>
      )}
    </Form>
  );
};

export default HouseholdGroupBilling;
